import { PageHierarchyNode } from '@/services/contentful/page-hierarchy-resolver'
import { i18nConfig } from '@/i18n/i18nConfig'

// BEGIN TEMP_FANFEST_SLUG_ALIAS (safe to delete after Contentful root slug = "fanfest" is published)
/**
 * TEMP fanfest path alias.
 *
 * Goal: keep public URLs/canonicals as `/fanfest/...` even while Contentful still stores the root slug
 * as `fanfest-2025` (needed for compatibility with the currently-live code).
 *
 * This affects canonical URLs, breadcrumbs, and any CMS-derived link generation that goes through
 * `pagePathFromSegments()`.
 *
 * Removal checklist:
 * - Verify Contentful root page slug is `fanfest` and published (in the environment this app uses).
 * - Delete *every* code block in this file wrapped in BEGIN/END TEMP_FANFEST_SLUG_ALIAS.
 * - No other edits should be necessary after deleting those blocks.
 */
const FANFEST_PUBLIC_SLUG = 'fanfest'
const FANFEST_LEGACY_SLUG = 'fanfest-2025'

function applyTemporarySlugAliases(segments: string[]): string[] {
  if (segments.length === 0) return segments

  // Only rewrite the root segment.
  if (segments[0] === FANFEST_LEGACY_SLUG) {
    return [FANFEST_PUBLIC_SLUG, ...segments.slice(1)]
  }

  return segments
}
// END TEMP_FANFEST_SLUG_ALIAS

export interface PageBreadcrumb {
  label: string
  href: string
}

export function buildBreadcrumbs(
  locale: string,
  ancestors: PageHierarchyNode[],
  page: PageHierarchyNode,
): PageBreadcrumb[] {
  const nodes = [...ancestors, page]
  const accumulatedSegments: string[] = []

  return nodes.reduce<PageBreadcrumb[]>((breadcrumbs, node) => {
    if (!node.slug) {
      return breadcrumbs
    }

    accumulatedSegments.push(node.slug)
    const label = node.title || node.slug

    breadcrumbs.push({
      label,
      href: pagePathFromSegments(locale, accumulatedSegments),
    })

    return breadcrumbs
  }, [])
}

export function pagePathFromSegments(locale: string, segments: string[]): string {
  let sanitized = segments.filter(Boolean)
  // BEGIN TEMP_FANFEST_SLUG_ALIAS (safe to delete after Contentful root slug = "fanfest" is published)
  // Keep public URLs as `/fanfest` while stored slug is `fanfest-2025`.
  sanitized = applyTemporarySlugAliases(sanitized)
  // END TEMP_FANFEST_SLUG_ALIAS
  const normalizedLocale = locale?.trim() || i18nConfig.defaultLocale || 'en'
  const prefixLocale = normalizedLocale !== (i18nConfig.defaultLocale || 'en')

  if (sanitized.length === 0) {
    return prefixLocale ? `/${normalizedLocale}` : '/'
  }

  return prefixLocale ? `/${[normalizedLocale, ...sanitized].join('/')}` : `/${sanitized.join('/')}`
}
